import React, {useEffect, useState} from "react";
import {Link as RouterLink, useNavigate} from "react-router-dom";
import {
    Box,
    Button,
    Checkbox,
    Container,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Link,
    TextField,
    Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {useTranslation} from "react-i18next";

import {login} from "../api/api";
import {useAuth} from "../context/AuthContext";

const LoginPage = () => {
    const {t} = useTranslation();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [keepLoggedIn, setKeepLoggedIn] = useState(false);
    const [error, setError] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    const navigate = useNavigate();
    const {refreshProfile} = useAuth();

    useEffect(() => {
        const token = localStorage.getItem("accessToken");
        if (token) navigate("/");
    }, [navigate]);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");

        try {
            await login(email, password);
            await refreshProfile();
            navigate("/", {replace: true});
        } catch (err: any) {
            setError(err.message ?? t("login.errors.default"));
        }
    };

    return (
        <Container maxWidth="xs">
            <Box py={10}>
                <Box p={3} boxShadow={3} borderRadius={2}>
                    <Typography variant="h5" gutterBottom align="center">
                        {t("login.title")}
                    </Typography>

                    {error && (
                        <Typography color="error" align="center">
                            {error}
                        </Typography>
                    )}

                    <form onSubmit={handleLogin}>
                        <TextField
                            fullWidth
                            label={t("login.email")}
                            type="email"
                            margin="normal"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />

                        <TextField
                            fullWidth
                            label={t("login.password")}
                            type={showPassword ? "text" : "password"}
                            margin="normal"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPassword((prev) => !prev)}
                                                edge="end"
                                                aria-label={
                                                    showPassword
                                                        ? t("login.hidePassword")
                                                        : t("login.showPassword")
                                                }
                                            >
                                                {showPassword ? <VisibilityOff/> : <Visibility/>}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />

                        <Box textAlign="right" mt={1}>
                            <Link
                                component={RouterLink}
                                to="/forgot-password"
                                underline="hover"
                                variant="body2"
                            >
                                {t("login.forgotPassword")}
                            </Link>
                        </Box>

                        <Box display="flex" alignItems="center" mt={1}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={keepLoggedIn}
                                        onChange={(e) => setKeepLoggedIn(e.target.checked)}
                                    />
                                }
                                label={t("login.keepMeLoggedIn")}
                            />
                        </Box>

                        <Button type="submit" fullWidth variant="contained" sx={{mt: 2}}>
                            {t("login.submit")}
                        </Button>
                    </form>
                </Box>
            </Box>
        </Container>
    );
};

export default LoginPage;